<template>
  <div>
    <i-split-button class="wide-split-button">
        <a class="main_split_button_action"
            :aria-label="mainAction.label"
            :href="mainAction.href"
            v-on="mainAction.handlers || {}">
            {{ mainAction.label }}
        </a>

        <template v-slot:dropdown>
            <i-menu-list class="dropdown-menu i-dropdown-menu block pull-right">
                <li v-for="{ label, href, icon, target, enabled, handlers={} } in dropdownActions" :key="label" role="presentation" :aria-label="label">
                  <a v-on="handlers" :href="href" :title="label" :class="{ 'i-state-disabled': !enabled }" :target="target || '_self'">
                      <i class="fa5 fas" :class="`fa-${icon}`" :name="icon"></i>
                      <span class="pl1">{{ label }}</span>
                  </a>
                </li>
            </i-menu-list>
        </template>
    </i-split-button>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import { mapState, mapGetters } from 'vuex';
import { mapBrowserState, mapBrowserActions, mapBrowserGetters } from '@/store';
import globals from '@/globals';
import { Tree } from '@/utils/tree';
import { ControllerParamFormat } from '@/components/browser/panel_center/PanelCenterFooterMassActions.vue';
import { BrowserMode, FinishController } from '@/store/modules/entity_browser/config';

const { uri } = globals.I.Util;

export default defineComponent({
  inheritAttrs: false,
  props: {
    item: Object,
  },
  computed: {
    ...mapState(['LANG', 'browserMode']),
    ...mapBrowserState(['isInsertMode']),
    ...mapBrowserGetters(['isHiddenField', 'tree']),
    ...mapGetters(['checkACL']),
    docRef() {
      return {
        nodeId: this.item.node_id,
        url: this.item.master ? null : this.item.url,
      };
    },
    mainAction() {
      return this.actions.find((action) => action.enabled) || this.actions[0];
    },
    dropdownActions() {
      return this.actions.filter((a) => a !== this.mainAction);
    },
    isImage() {
      if (!this.item.mime_type) {
        return false;
      }

      return this.item.mime_type.match(/^image.(?!svg)/);
    },
    isImperiaDocument() {
      if (!this.item.mime_type) {
        return false;
      }

      return this.item.mime_type.match('text/html');
    },
    documentTreeNode() {
      const nodes = this.tree.nodes || [];
      const node = Tree.find(nodes, (n) => n.id === this.item.cat_id);

      return node || {};
    },
    actions() {
      const editOrDesktop = this.item.is_on_desktop ? 'desktop' : 'edit';

      const actionIds = {
        [BrowserMode.DOC]: [
          ...(this.isInsertMode ? ['insert'] : []),
          editOrDesktop,
          ...(!this.item.is_on_desktop ? ['quickedit'] : []),
          'publish',
          'info',
          ...(this.isImperiaDocument ? ['differences'] : []),
          'history',
          'preview',
          'reparse',
          'metaviewer',
          'unpublish',
          'delete',
          'archive',
        ],
        [BrowserMode.MAM]: [
          ...(this.isInsertMode ? ['insert'] : []),
          editOrDesktop,
          'variants',
          ...(this.isImage ? ['focuspoint'] : []),
          'customize',
          'info',
          ...(this.isImperiaDocument ? ['differences'] : []),
          ...(this.isImage ? ['zoom'] : []),
          'publish',
          'metaviewer',
          'unpublish',
          'delete',
          'archive',
        ],
      };

      return actionIds[this.browserMode]
        .filter((id) => !this.isHiddenField(id))
        .map((id) => this.actionsConfig[id] || { label: id });
    },
    actionsConfig() {
      return {
        insert: {
          label: this.LANG.insert,
          icon: 'images',
          handlers: {
            click: (event) => {
              event.preventDefault();

              const {
                node_id: nodeId,
                insert_url: insertUrl,
                url,
                filesize,
              } = this.item;

              this.insertVariant({
                nodeId,
                variantUri: insertUrl || url,
                contentLength: filesize,
              });
            },
          },
          enabled: true,
        },
        desktop: {
          label: this.LANG.desktop,
          icon: 'desktop',
          href: uri('/document/desktop', { search: this.item.node_id }),
          enabled: this.checkACL('/document/desktop'),
        },
        edit: {
          label: this.LANG.edit,
          icon: 'info-circle',
          href: uri('/document/archive/safe_import', { __imperia_method: 'edit', __imperia_node_id: this.item.node_id, __imperia_arch_id: this.item.arch_id }),
          enabled: this.checkACL('/document/archive/import') && this.item.can_edit,
        },
        quickedit: {
          label: this.LANG.quickedit,
          icon: 'pen-square',
          href: uri('/document/archive/safe_import', { __imperia_method: 'quickEdit', __imperia_node_id: this.item.node_id, __imperia_arch_id: this.item.arch_id }),
          enabled: this.checkACL('/document/archive/import') && this.item.can_edit,
        },
        zoom: {
          label: this.LANG.zoom,
          icon: 'search',
          href: this.item.url,
          target: '_blank',
          enabled: true && this.isImage,
        },
        preview: {
          label: this.LANG.preview,
          icon: 'sun',
          href: this.item.uri_id !== -1
            ? uri('/document/preview_url', { __imperia_uri_id: this.item.uri_id })
            : uri('/document/preview', { __imperia_node_id: this.item.node_id, __imperia_action: '__preview' }),
          target: '_blank',
          enabled: this.checkACL('/document/preview_url'),
        },
        info: {
          label: this.LANG.information,
          icon: 'info-circle',
          href: uri('/document/oce/info', { __imperia_node_id: this.item.node_id }),
          enabled: this.checkACL('/document/oce/info'),
          handlers: {
            click: (event) => {
              event.preventDefault();
              this.$emit('documentAction', { action: 'info', document: this.item });
            },
          },
        },
        metaviewer: {
          label: this.LANG.metaviewer,
          icon: 'info-circle',
          href: uri('/document/media/details/published', { node_id: this.item.node_id, generate_details: '1' }),
          enabled: this.checkACL('/document/media/details'),
          target: '_blank',
        },
        delete: {
          label: this.LANG.delete,
          icon: 'times',
          href: uri('/document/media/content/published/mass_action/delete', {
            ...ControllerParamFormat.delete(this.docRef),
            finish_controller: FinishController[this.browserMode],
          }),
          enabled: this.checkACL('/document/media/content/published/mass_action/delete') && this.item.can_delete,
        },
        differences: {
          label: this.LANG.differences,
          icon: 'ballot-check',
          href: uri('/document/diff_select', { __imperia_node_id: this.item.node_id }),
          enabled: this.checkACL('/document/diff_select'),
          handlers: {
            click: (event) => {
              event.preventDefault();
              this.$emit('documentAction', { action: 'differences', document: this.item });
            },
          },
        },
        publish: {
          label: this.LANG.publish,
          icon: 'globe',
          href: uri('/document/publisher', {
            ...ControllerParamFormat.publish(this.docRef),
            ignore_cache: '1',
            finish_controller: FinishController[this.browserMode],
          }),
          enabled: this.checkACL('/document/publisher') && this.item.can_edit && this.item.uri_id > 0,
        },
        unpublish: {
          label: this.LANG.unpublish,
          icon: 'download',
          href: uri('/document/publisher', {
            ...ControllerParamFormat.unpublish(this.docRef),
            ignore_cache: '1',
            finish_controller: FinishController[this.browserMode],
          }),
          enabled: this.checkACL('/document/publisher') && this.item.can_delete && Boolean(this.item.published_at),
        },
        reparse: {
          label: this.LANG.reparse,
          icon: 'sync',
          href: uri('/structure/template_reparser/reparse', { node_id: this.item.node_id, type: 'generate', mode: 'pending' }),
          enabled: this.checkACL('/structure/template_reparser/reparse') && this.item.can_edit,
        },
        focuspoint: {
          label: this.LANG.focusPoint,
          icon: 'tasks',
          href: uri('/document/media/focus_area/show', { node_id: this.item.node_id }),
          enabled: this.checkACL('/document/media/focus_area/show') && this.isImage,
          handlers: {
            click: (event) => {
              event.preventDefault();
              this.$emit('documentAction', { action: 'focusPoint', document: this.item });
            },
          },
        },
        customize: {
          label: this.LANG.customize,
          icon: 'edit',
          href: '#',
          enabled: this.checkACL('/document/media/customize') && this.item.can_edit,
          handlers: {
            click: (event) => {
              event.preventDefault();

              globals.I.MAM.showActions(this.item.node_id);
            },
          },
        },
        variants: {
          label: this.LANG.viewVariants,
          icon: 'eye',
          href: '#',
          enabled: this.checkACL('/document/media/show_variants'),
          handlers: {
            click: (event) => {
              event.preventDefault();
              this.$emit('documentAction', { action: 'variants', document: this.item });
            },
          },
        },
        history: {
          label: this.LANG.history,
          icon: 'history',
          href: uri('/document/view_history', { __imperia_node_id: this.item.node_id, standalone: '1' }),
          enabled: this.checkACL('/document/view_history'),
          handlers: {
            click: (event) => {
              event.preventDefault();
              this.$emit('documentAction', { action: 'history', document: this.item });
            },
          },
        },
        archive: {
          label: this.LANG.showArchive,
          icon: 'archive',
          href: '',
          enabled: this.checkACL('/data/documents/archive_versions'),
          handlers: {
            click: (event) => {
              event.preventDefault();
              this.$emit('documentAction', { action: 'archive', document: this.item });
            },
          },
        },
      };
    },
  },
  methods: {
    ...mapBrowserActions(['insertVariant']),
  },
});
</script>

<style scoped lang='scss'>
.wide-split-button {
  min-width: 140px;
  margin: 0;
}
/* FIXME: support focus ring */
a.main_split_button_action:focus {
  outline: 0;
}
</style>
